<template>
  <div class="treatment-item-input-table">
    <div class="table">
      <div class="table-header">
        <div class="cell name">診療項目内容</div>
        <div class="cell insurance-flg">保険</div>
        <div class="cell tax-exempt-flg">非課税</div>
        <div class="cell unit-price">単価（￥）</div>
        <div class="cell amount">数量</div>
        <div class="cell discount-rate">割引（%）</div>
        <div class="cell discount-price">値引（￥）</div>
        <div class="cell subtotal }">計</div>
      </div>
      <div class="table-body">
        <div class="cell name">
          <div class="text" :title="treatmentItem.name">
            {{ treatmentItem.name }}
          </div>
          <div
            v-if="isMedicine && !detailFlg"
            class="image-wrap plus"
            data-test="plus-button"
            @click="$emit('click-plus')"
          >
            <img
              class="image plus"
              src="@/assets/images/circle_plus_orange.png"
            />
          </div>
        </div>
        <div class="cell insurance-flg">
          <base-select-box
            :selectData="selectData"
            :styles="{ height: '28px' }"
            v-model="treatmentItem.insuranceFlg"
          />
        </div>
        <div class="cell tax-exempt-flg">
          <base-select-box
            :selectData="selectData"
            :styles="{ height: '28px' }"
            v-model="treatmentItem.taxExemptFlg"
          />
        </div>
        <div class="cell unit-price">
          <validation-provider
            class="validation-provider"
            :rules="{ intRule: true }"
            v-slot="{ errors }"
            ><base-text-box
              :value="treatmentItem.unitPrice"
              :maxlength="7"
              :styles="textBoxStyles"
              :fullWidthNumbersToHalfWidthNumbers="true"
              @input="inputNum($event, 'unitPrice')"
              @blur="blur('unitPrice')"
              data-test="unit-price-text-box"
            />
            <div v-if="errors[0]" class="error" data-test="unit-price-error">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>
        <div class="cell amount">
          <validation-provider
            class="validation-provider"
            :rules="{ floatRule: isMedicine, intRule: !isMedicine }"
            v-slot="{ errors }"
            ><base-text-box
              data-test="amount-text-box"
              :value="treatmentItem.amount"
              :maxlength="5"
              :styles="textBoxStyles"
              :fullWidthNumbersToHalfWidthNumbers="true"
              @input="inputNum($event, 'amount')"
              @blur="blur('amount')"
            />
            <div v-if="errors[0]" class="error" data-test="amount-error">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>
        <div class="cell discount-rate">
          <validation-provider
            class="validation-provider"
            :rules="{ intRule: true, percentRule: true }"
            v-slot="{ errors }"
            ><base-text-box
              data-test="discount-rate-text-box"
              :value="treatmentItem.discountRate"
              :maxlength="3"
              :styles="textBoxStyles"
              :fullWidthNumbersToHalfWidthNumbers="true"
              @input="inputNum($event, 'discountRate')"
              @blur="blur('discountRate')"
            />
            <div v-if="errors[0]" class="error" data-test="discount-rate-error">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>
        <div class="cell discount-price">
          <validation-provider
            class="validation-provider"
            :rules="{ intRule: true }"
            v-slot="{ errors }"
            ><base-text-box
              data-test="discount-price-text-box"
              :value="treatmentItem.discountPrice"
              :maxlength="7"
              :styles="textBoxStyles"
              :fullWidthNumbersToHalfWidthNumbers="true"
              @input="inputNum($event, 'discountPrice')"
              @blur="blur('discountPrice')"
            />
            <div
              v-if="errors[0]"
              class="error"
              data-test="discount-price-error"
            >
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>
        <div class="cell subtotal">
          <div class="text number" :title="subtotal" data-test="subtotal">
            ￥{{ subtotal.toLocaleString() }}
          </div>
        </div>
      </div>
      <div v-if="prescriptionFlg" class="table-footer">
        <div class="cell prescription">
          <div
            class="label"
            :class="{ 'is-detail': detailFlg, 'is-normal': !detailFlg }"
          >
            <div class="title-text">処方</div>
          </div>
          <div class="value">
            <validation-provider
              class="validation-provider"
              :rules="{ notSurrogatePair: true, maxRule: 255 }"
              v-slot="{ errors }"
              ><base-multiple-lines-text-box
                class="text-form"
                data-test="prescription-textarea"
                :maxlength="255"
                :placeholder="'例 １回 ○ g １日△回 朝夕食後 ×日分'"
                :styles="{
                  width: '570px',
                  height: '84px',
                  padding: '5px 20px 5px 8px'
                }"
                v-model="treatmentItem.prescription"
              />
              <div
                v-if="errors[0]"
                class="error"
                data-test="prescription-error"
              >
                {{ errors[0] }}
              </div>
            </validation-provider>
            <div class="prescription-buttons">
              <div class="small-white-buttons">
                <base-button-small-white
                  class="small-white-button"
                  data-test="memo-copy-button"
                  @click="$emit('click-copy-memo')"
                  :styles="{ width: '80px', padding: '3px' }"
                  >メモ反映</base-button-small-white
                >
                <base-button-small-white
                  class="small-white-button"
                  data-test="clear-button"
                  @click="$emit('click-clear')"
                  :styles="{ width: '80px', padding: '3px' }"
                  >クリア</base-button-small-white
                >
              </div>
              <base-button-trash
                v-if="!detailFlg"
                class="trash-button"
                @click="deletePrescription"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButtonSmallWhite from '@/components/parts/atoms/BaseButtonSmallWhite.vue'
import BaseSelectBox from '@/components/parts/atoms/BaseSelectBox'
import BaseTextBox from '@/components/parts/atoms/BaseTextBox'
import BaseButtonTrash from '@/components/parts/atoms/BaseButtonTrash'
import BaseMultipleLinesTextBox from '@/components/parts/atoms/BaseMultipleLinesTextBox.vue'
import { ValidationProvider } from 'vee-validate'
import '@/utils/validation_rules'
import { calculateTreatmentItemSubtotal } from '@/utils/price_calculation'

export default {
  name: 'TreatmentItemInputTable',

  components: {
    BaseSelectBox,
    BaseTextBox,
    BaseButtonTrash,
    BaseMultipleLinesTextBox,
    ValidationProvider,
    BaseButtonSmallWhite
  },

  props: {
    treatmentItem: { type: Object },
    detailFlg: { type: Boolean, default: false },
    insuranceType: { type: String, default: '' }
  },

  data() {
    return {
      textBoxStyles: { width: '80px', height: '28px', textAlign: 'right' },
      selectData: [
        { id: 1, name: '〇' },
        { id: 0, name: '✕' }
      ],
      popupFlg: false,
      prescriptionFlg: false
    }
  },

  computed: {
    isMedicine() {
      return (
        this.treatmentItem.medicineId > 0 ||
        this.treatmentItem.medicineOriginalId > 0
      )
    },
    existsPrescription() {
      return this.isMedicine && this.treatmentItem.prescription.length > 0
    },
    subtotal() {
      return calculateTreatmentItemSubtotal(
        this.insuranceType,
        this.treatmentItem
      )
    }
  },

  created() {
    this.prescriptionFlg = this.existsPrescription || this.detailFlg
  },

  methods: {
    deletePrescription() {
      this.$emit('click-trash')
      this.prescriptionFlg = false
    },
    blur(field) {
      if (this.treatmentItem[field] === '') this.treatmentItem[field] = 0
    },
    inputNum(value, field) {
      const isInt = /^([0-9]\d*)$/.test(value)
      const newValue = isInt ? Number(value) : value
      this.treatmentItem[field] = newValue
    }
  }
}
</script>

<style lang="scss" scoped>
$border-color: #{$light-grey};
@mixin center {
  display: flex;
  justify-content: center;
  align-items: center;
}
.treatment-item-input-table {
  > .table {
    width: 834px;
    > .table-header {
      box-sizing: border-box;
      display: flex;
      justify-content: space-between;
      width: 100%;
      background-color: #{$very-light-shade-orange};
      border: solid $border-color;
      border-width: 1px 1px 1px 1px;
      > .cell {
        box-sizing: border-box;
        width: 90px;
        padding: 0;
        line-height: 28px;
        border-right: solid 1px #{$light-grey};
        font-size: 13px;
        text-align: center;
        font-feature-settings: 'palt';
        &:last-child {
          border-right: none;
        }
        &.name {
          width: 254px;
          padding: 0 8px;
          text-align: left;
        }
        &.insurance-flg,
        &.tax-exempt-flg {
          width: 60px;
        }
        &.subtotal {
          width: 100px;
        }
      }
    }
    > .table-body {
      box-sizing: border-box;
      display: flex;
      justify-content: space-between;
      width: 100%;
      background-color: #{$white};
      border: solid $border-color;
      border-width: 0 1px 1px 1px;
      > .cell {
        box-sizing: border-box;
        @include center;
        width: 90px;
        height: 34px;
        border-right: solid 1px #{$light-grey};
        font-size: 13px;
        &:last-child {
          border-right: none;
        }
        &.name {
          width: 254px;
        }
        &.insurance-flg,
        &.tax-exempt-flg {
          width: 60px;
        }
        &.subtotal {
          width: 100px;
        }
        > .text {
          flex: 1;
          line-height: 34px;
          padding: 0 8px;
          text-align: left;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          &.number {
            text-align: right;
          }
        }
        > .image-wrap {
          @include center;
          width: 24px;
          height: 24px;
          cursor: pointer;
          z-index: 1;
          border-radius: 50%;
          &.plus:hover {
            > .image {
              border-radius: 50%;
              box-shadow: 0 0 4px;
            }
          }
        }
      }
    }
    > .table-footer {
      > .prescription {
        box-sizing: border-box;
        display: flex;
        width: 100%;
        height: 95px;
        background-color: #{$white};
        border: solid $border-color;
        border-width: 0 1px 1px 1px;
        > .label {
          @include center;
          &.is-detail {
            width: 150px;
          }
          &.is-normal {
            width: 110px;
          }
          font-size: 13px;
          background-color: #{$very-light-shade-orange};
          border-right: solid 1px #{$light-grey};
          position: relative;
        }
        > .value {
          flex: 1;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          padding-left: 6px;
          position: relative;
          > .prescription-buttons {
            justify-content: flex-end;
            align-items: center;
            display: flex;
            gap: 8px;
            margin-left: 10px;
            > .small-white-buttons {
              flex-direction: column;
              gap: 6px;
              padding: 6px 0;
              > .small-white-button {
                width: 80px;
                padding: 3px;
              }
            }
          }
        }
      }
    }
  }
}
.validation-provider {
  position: relative;
  > .error {
    position: absolute;
    z-index: 10;
    height: 18px;
    line-height: 18px;
    padding: 0 3px;
    background-color: #{$white};
    color: #{$tomato};
    font-size: 12px;
    text-align: left;
    white-space: nowrap;
  }
}
</style>
